import type { FC } from 'react'
import { useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { Check, LockOutlined } from '@mui/icons-material'
import type {
  ClaudeSession,
  ConversationTurnEntry,
} from '@/features/events/conversation/state/types'
import type { AgentMode, EventType, HookType } from '@/features/events/shared/types'
import type { EncryptionStatus } from '@/features/events/shared/types/EncryptionInfo'
import { rebuildClaudeCodeEventDetailsFrom } from '@/features/events/conversation/models'
import {
  eventTypeToString,
  resolveEventTypeLabelFor,
} from '@/features/events/shared/types/enums/EventType'
import {
  agentModeToString,
  resolveAgentModeBadgeLabel,
} from '@/features/events/shared/types/enums/AgentMode'
import {
  hookTypeToString,
  resolveHookTypeLabelFor,
} from '@/features/events/shared/types/enums/HookType'
import { formatMcpToolForDisplay } from '@/features/events/shared/utils'
import { CopyableText, CopyButton, standardTooltipProps } from '@/common/components'
import { useCopyToClipboard } from '@/common/hooks'
import { BaseDialog } from '.'
import { expandControlIconButtonSx } from '../controls'

type ValueColor = 'success' | 'warning' | 'default'

interface EventDetailsDialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly event: ConversationTurnEntry
  readonly session: ClaudeSession
}

interface SectionHeaderProps {
  readonly title: string
}

interface DetailRowProps {
  readonly label: string
  readonly value: string
  readonly valueColor?: ValueColor
}

interface ClickableValueProps {
  readonly label: string
  readonly value: string
}

interface EventDetailsContentProps {
  readonly event: ConversationTurnEntry
  readonly session: ClaudeSession
}

interface EventDetailsData {
  readonly sessionMode: string
  readonly sessionModeColor: ValueColor
  readonly agentMode: string
  readonly hookType: string
  readonly toolName: string
  readonly eventType: string
  readonly timestamp: string
  readonly host: string
  readonly workingDirectory: string
  readonly tmuxSession: {
    readonly sessionName: string
    readonly sessionId: string
    readonly paneId: string
  } | null
  readonly encryption: EncryptionStatus
  readonly userExternalId: string
  readonly hostId: string
  readonly sessionId: string
  readonly userPromptId: string
  readonly eventId: string
}

export const EventDetailsDialog: FC<EventDetailsDialogProps> = ({
  open,
  onClose,
  event,
  session,
}) => {
  const { copied: copiedJson, copy: copyJson } = useCopyToClipboard()

  const formattedText = useMemo(() => formatEventDetails(event, session), [event, session])

  const jsonText = useMemo(() => {
    const claudeCodeEvent = rebuildClaudeCodeEventDetailsFrom(event, session)
    return JSON.stringify(claudeCodeEvent, serializeEnumsToStrings, 2)
  }, [event, session])

  const controlsRow = useMemo(() => {
    const handleJsonCopy = () => {
      void copyJson(jsonText)
    }

    return (
      <Box sx={controlsRowContainerSx}>
        <CopyButton text={formattedText} tooltipText="Copy details" />
        <Tooltip {...standardTooltipProps} title="Copy JSON">
          <IconButton onClick={handleJsonCopy} size="small" sx={expandControlIconButtonSx}>
            {copiedJson ? (
              <Check fontSize="small" sx={jsonCopiedIconSx} />
            ) : (
              <Box component="span" sx={jsonIconSx}>
                {'{}'}
              </Box>
            )}
          </IconButton>
        </Tooltip>
      </Box>
    )
  }, [formattedText, jsonText, copyJson, copiedJson])

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="Event details"
      breadcrumb={session.sessionBreadcrumb}
      controlsRow={controlsRow}
      dialogWidth="900px"
      dialogHeight="auto"
    >
      <EventDetailsContent event={event} session={session} />
    </BaseDialog>
  )
}

const EventDetailsContent: FC<EventDetailsContentProps> = ({ event, session }) => {
  const data = extractEventDetailsData(event, session)

  return (
    <Box>
      {/* Section 1: Event Context */}
      <Box sx={sectionSx}>
        <SectionHeader title="Event Context" />
        <DetailRow
          label="Session mode"
          value={data.sessionMode}
          valueColor={data.sessionModeColor}
        />
        <DetailRow label="Agent mode" value={data.agentMode} />
        <DetailRow label="Hook type" value={data.hookType} />
        <DetailRow label="Tool" value={data.toolName} />
        <DetailRow label="Event type" value={data.eventType} />
        <DetailRow label="Timestamp" value={data.timestamp} />
      </Box>

      {/* Section: Encryption */}
      <Box sx={sectionSx}>
        <SectionHeader title="Encryption" />
        {data.encryption ? (
          <>
            <Box sx={detailRowSx}>
              <Typography component="span" sx={labelSx}>
                Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LockOutlined sx={{ fontSize: '0.875rem', color: 'success.main' }} />
                <Typography component="span" sx={valueSuccessSx}>
                  End-to-end encrypted
                </Typography>
              </Box>
            </Box>
            <DetailRow label="Algorithm" value={data.encryption.algorithm} />
            <DetailRow label="Key version" value={String(data.encryption.keyVersion)} />
          </>
        ) : (
          <Box sx={detailRowSx}>
            <Typography component="span" sx={labelSx}>
              Status
            </Typography>
            <Typography component="span" sx={valueDisabledSx}>
              Not encrypted
            </Typography>
          </Box>
        )}
      </Box>

      {/* Section 2: Environment */}
      <Box sx={sectionSx}>
        <SectionHeader title="Environment" />
        <DetailRow label="Host" value={data.host} />
        <DetailRow label="Working dir" value={data.workingDirectory} />
      </Box>

      {/* Section 3: Terminal Session (only if tmux available) */}
      {data.tmuxSession && (
        <Box sx={sectionSx}>
          <SectionHeader title="Terminal Session" />
          <ClickableValue label="Session name" value={data.tmuxSession.sessionName} />
          <ClickableValue label="Session ID" value={data.tmuxSession.sessionId} />
          <ClickableValue label="Pane ID" value={data.tmuxSession.paneId} />
        </Box>
      )}

      {/* Section 4: Identifiers (broadest to most specific) */}
      <Box sx={sectionSx}>
        <SectionHeader title="Identifiers" />
        <ClickableValue label="User External ID" value={data.userExternalId} />
        <ClickableValue label="Host ID" value={data.hostId} />
        <ClickableValue label="Session ID" value={data.sessionId} />
        {data.userPromptId !== 'Not available' && (
          <ClickableValue label="User Prompt ID" value={data.userPromptId} />
        )}
        <ClickableValue label="Event ID" value={data.eventId} />
      </Box>

      {/* Footer Help Text */}
      <Typography sx={helpTextSx}>Click any identifier to copy it to clipboard</Typography>
    </Box>
  )
}

const SectionHeader: FC<SectionHeaderProps> = ({ title }) => (
  <Typography variant="overline" sx={sectionHeaderSx}>
    {title}
  </Typography>
)

const DetailRow: FC<DetailRowProps> = ({ label, value, valueColor = 'default' }) => {
  const getValueSx = (): SxProps<Theme> => {
    switch (valueColor) {
      case 'success':
        return valueSuccessSx
      case 'warning':
        return valueWarningSx
      default:
        return valueSx
    }
  }

  return (
    <Box sx={detailRowSx}>
      <Typography component="span" sx={labelSx}>
        {label}
      </Typography>
      <Typography component="span" sx={getValueSx()}>
        {value}
      </Typography>
    </Box>
  )
}

const ClickableValue: FC<ClickableValueProps> = ({ label, value }) => (
  <Box sx={clickableRowSx}>
    <Typography component="span" sx={labelSx}>
      {label}
    </Typography>
    <CopyableText
      text={value}
      containerSx={clickableValueContainerSx}
      textSx={clickableValueSx}
      checkmarkSx={clickableCheckmarkSx}
    >
      {value}
    </CopyableText>
  </Box>
)

function serializeEnumsToStrings(key: string, value: unknown): unknown {
  if (key === 'agentMode' && typeof value === 'number') {
    return agentModeToString(value as AgentMode)
  }

  if (key === 'eventType' && typeof value === 'number') {
    return eventTypeToString(value as EventType)
  }

  if (key === 'hookType' && typeof value === 'number') {
    return hookTypeToString(value as HookType)
  }

  return value
}

function getUserPromptId(event: ConversationTurnEntry, session: ClaudeSession): string {
  for (const [userPromptId, turn] of session.conversationTurns) {
    if (turn.events.some((e) => e.eventId === event.eventId)) {
      return userPromptId
    }
  }
  return 'Not available'
}

function formatTimestamp(timestamp: string): string {
  return new Date(timestamp).toLocaleString()
}

interface SessionModeInfo {
  readonly label: string
  readonly color: ValueColor
}

function deriveSessionMode(dangerouslySkipPermissions: boolean | undefined): SessionModeInfo {
  if (dangerouslySkipPermissions === undefined) {
    return { label: 'Unknown', color: 'default' }
  }
  return dangerouslySkipPermissions
    ? { label: 'Dangerous', color: 'warning' }
    : { label: 'Safe', color: 'success' }
}

function formatToolNameForDisplay(toolName: string | null): string {
  if (!toolName) return '-'
  const mcpFormatted = formatMcpToolForDisplay(toolName)
  if (mcpFormatted) {
    return `${mcpFormatted.toolDisplayName}: ${mcpFormatted.actionDisplayName}`
  }
  return toolName
}

function extractEventDetailsData(
  event: ConversationTurnEntry,
  session: ClaudeSession,
): EventDetailsData {
  const tmux = session.tmuxSession
  const sessionModeInfo = deriveSessionMode(session.dangerouslySkipPermissions)

  return {
    sessionMode: sessionModeInfo.label,
    sessionModeColor: sessionModeInfo.color,
    agentMode: resolveAgentModeBadgeLabel(event.agentMode),
    hookType: event.hookType === null ? '-' : resolveHookTypeLabelFor(event.hookType),
    toolName: formatToolNameForDisplay(event.toolName),
    eventType: resolveEventTypeLabelFor(event.eventType),
    timestamp: formatTimestamp(event.timestamp),
    host: session.userHostDetails.hostName,
    workingDirectory: session.contextWorkDirectory || 'Not specified',
    encryption: event.encryption,
    tmuxSession: tmux
      ? {
          sessionName: tmux.sessionName,
          sessionId: tmux.sessionId,
          paneId: tmux.paneId,
        }
      : null,
    userExternalId: session.userExternalId,
    hostId: session.userHostDetails.hostId,
    sessionId: session.sessionId,
    userPromptId: getUserPromptId(event, session),
    eventId: event.eventId,
  }
}

function formatEventDetails(event: ConversationTurnEntry, session: ClaudeSession): string {
  const data = extractEventDetailsData(event, session)

  let text = `Event Context
─────────────
Session mode: ${data.sessionMode}
Agent mode: ${data.agentMode}
Hook type: ${data.hookType}
Tool: ${data.toolName}
Event type: ${data.eventType}
Timestamp: ${data.timestamp}`

  if (data.encryption) {
    text += `

Encryption
──────────
Status: End-to-end encrypted
Algorithm: ${data.encryption.algorithm}
Key version: ${data.encryption.keyVersion}`
  } else {
    text += `

Encryption
──────────
Status: Not encrypted`
  }

  text += `

Environment
───────────
Host: ${data.host}
Working directory: ${data.workingDirectory}`

  if (data.tmuxSession) {
    text += `

Terminal Session
────────────────
Session name: ${data.tmuxSession.sessionName}
Session ID: ${data.tmuxSession.sessionId}
Pane ID: ${data.tmuxSession.paneId}`
  }

  text += `

Identifiers
───────────
User External ID: ${data.userExternalId}
Host ID: ${data.hostId}
Session ID: ${data.sessionId}
User Prompt ID: ${data.userPromptId}
Event ID: ${data.eventId}`

  return text
}

const controlsRowContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-start',
  gap: 0.5,
}

const jsonCopiedIconSx: SxProps<Theme> = {
  color: 'success.main',
}

const jsonIconSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '1.15rem',
  fontWeight: 700,
  color: 'text.primary',
}

const sectionSx: SxProps<Theme> = {
  mb: 2.5,
}

const sectionHeaderSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  mb: 1,
  display: 'block',
}

const detailRowSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '140px 1fr',
  gap: 2,
  mb: 0.75,
}

const labelSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  color: 'text.secondary',
}

const valueSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  color: 'text.primary',
}

const valueSuccessSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  color: 'success.main',
}

const valueWarningSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  color: 'warning.main',
}

const valueDisabledSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  color: 'text.disabled',
}

const clickableRowSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '140px 1fr',
  gap: 2,
  mb: 0.75,
}

const clickableValueContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

const clickableCheckmarkSx: SxProps<Theme> = {
  color: 'success.main',
  fontSize: '1rem',
}

const clickableValueSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  color: 'primary.main',
  opacity: 0.5,
  cursor: 'pointer',
  transition: 'opacity 0.2s',
  '&:hover': {
    opacity: 1,
  },
}

const helpTextSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  color: 'text.secondary',
  fontStyle: 'italic',
  mt: 2,
  textAlign: 'center',
}
